import { Box } from '@interstate/components/Box';
import type { InterstateOnChangeCallback } from '@interstate/components/InterstateEvents';
import type { SelectInputEventValue } from '@interstate/components/SelectInput';
import { SelectInput } from '@interstate/components/SelectInput';
import type { TermMonthItem } from '@makemydeal/dr-platform-types';
import { FlexBuyAvailableMonths } from '@makemydeal/dr-platform-types';
import { kebabCase } from 'lodash';
import React, { useMemo, useState } from 'react';
import { FLEX_BUY_PERCENT_LABEL, GENERIC_LAST_FIELD_CHANGED_ERROR, TERM } from '../../constants';
import { flexBuyPercentOptions, flexBuyTermOptions } from '../../flexBuy/constants';
import { GridItem } from './styled.components';
import { useSelector } from 'react-redux';
import * as compositeSelectors from '@makemydeal/dr-dash-store/selectors/compositeSelectors';
import * as flexBuyPaymentActionTypes from '@makemydeal/dr-dash-store/actionTypes/flexBuyPaymentActionTypes';
import * as offerActionTypes from '@makemydeal/dr-dash-store/actionTypes/offerActionTypes';

type FlexBuyTermAndPercentInputsProps = {
    term: TermMonthItem;
    flexBuyPercent: TermMonthItem | null;
    onChangeTerm: (term: TermMonthItem, percents: TermMonthItem) => void;
    onChangeFlexBuyPercent: (percent: TermMonthItem) => void;
    isLoadingGrid?: boolean;
};

const hasOneElement = (arr: any[]) => arr.length === 1;

const defaultPercentValueFor66 = flexBuyPercentOptions[FlexBuyAvailableMonths.MONTH_66][0].value;
const defaultPercentValueFor75 = flexBuyPercentOptions[FlexBuyAvailableMonths.MONTH_75][0].value;

export const FlexBuyTermAndPercentInputs: React.FC<FlexBuyTermAndPercentInputsProps> = ({
    term,
    flexBuyPercent,
    onChangeTerm,
    onChangeFlexBuyPercent,
    isLoadingGrid
}) => {
    const defaultTermNumber = (term.val || FlexBuyAvailableMonths.MONTH_66) as FlexBuyAvailableMonths;
    const defaultFlexBuyPercent = flexBuyPercent?.val?.toString() || defaultPercentValueFor66;

    const isActionSelectedTermFailed = useSelector(compositeSelectors.getLastFailureActionIfError(offerActionTypes.SELECTED_TERM));
    const isActionUpdateFFBFailed = useSelector(
        compositeSelectors.getLastFailureActionIfError(flexBuyPaymentActionTypes.UPDATE_FLEX_BUY_PAYMENT)
    );
    const [termNumber, setTermNumber] = useState<FlexBuyAvailableMonths>(defaultTermNumber);
    const [percent, setPercent] = useState<string>(defaultFlexBuyPercent);

    const percentOptions = useMemo(() => flexBuyPercentOptions[termNumber] || [], [termNumber]);
    const isSingle75MonthChoice = hasOneElement(percentOptions) && termNumber === FlexBuyAvailableMonths.MONTH_75;
    const isDisabledPercentInput = isSingle75MonthChoice || !percentOptions.length || isLoadingGrid;

    const actionSelectedTermError =
        isActionSelectedTermFailed || isActionUpdateFFBFailed ? GENERIC_LAST_FIELD_CHANGED_ERROR : undefined;

    const handleChangeTerm: InterstateOnChangeCallback<SelectInputEventValue> = (event) => {
        const newTermValue = event.target.value;
        if (!newTermValue) return;

        const parsedTermValue = Number(newTermValue);
        if (parsedTermValue === termNumber || isNaN(parsedTermValue)) {
            return;
        }

        const termAndPercentageInputs: {
            term: TermMonthItem;
            percent: TermMonthItem | null;
        } = {
            term: {
                ...term,
                val: parsedTermValue
            },
            percent: null
        };
        let defaultPercentValue;

        if (parsedTermValue === FlexBuyAvailableMonths.MONTH_66) {
            defaultPercentValue = defaultPercentValueFor66;
        } else if (parsedTermValue === FlexBuyAvailableMonths.MONTH_75) {
            defaultPercentValue = defaultPercentValueFor75;
        }

        setTermNumber(parsedTermValue as FlexBuyAvailableMonths);

        if (defaultPercentValue && flexBuyPercent) {
            setPercent(defaultPercentValue);
            termAndPercentageInputs.percent = {
                ...flexBuyPercent,
                val: Number(defaultPercentValue)
            };
        }

        if (termAndPercentageInputs.percent) {
            onChangeTerm(termAndPercentageInputs.term, termAndPercentageInputs.percent);
        }
    };

    const handleChangePercent: InterstateOnChangeCallback<SelectInputEventValue> = (event) => {
        if (isSingle75MonthChoice) return;

        const selectedFlexBuyPercent = event.target.value;

        if (!selectedFlexBuyPercent) {
            return;
        }

        setPercent(selectedFlexBuyPercent);
        if (flexBuyPercent) {
            onChangeFlexBuyPercent({
                ...flexBuyPercent,
                val: Number(selectedFlexBuyPercent)
            });
        }
    };

    return (
        <GridItem xs={3} className="payment-grid-item" sx={{ height: '128px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', gap: '8px' }}>
                <SelectInput
                    data-testid={kebabCase(`${TERM} (mo) ${term.key}`)}
                    disabled={isLoadingGrid}
                    displayDeselectOption={false}
                    errorMessage={actionSelectedTermError}
                    hasError={isActionSelectedTermFailed}
                    id={`${TERM} (mo)`}
                    label={`${TERM} (mo)`}
                    name={kebabCase(`${TERM} (mo)`)}
                    onChange={handleChangeTerm}
                    options={flexBuyTermOptions}
                    placeholder="Select Term"
                    size="sm"
                    value={`${termNumber}`}
                />
                <SelectInput
                    data-testid={kebabCase(`${FLEX_BUY_PERCENT_LABEL} ${term.key}`)}
                    disabled={isDisabledPercentInput}
                    displayDeselectOption={false}
                    errorMessage={actionSelectedTermError}
                    hasError={isActionUpdateFFBFailed}
                    id={FLEX_BUY_PERCENT_LABEL}
                    label={FLEX_BUY_PERCENT_LABEL}
                    name={kebabCase(FLEX_BUY_PERCENT_LABEL)}
                    onChange={handleChangePercent}
                    options={percentOptions}
                    placeholder="Select Flex Buy Percent"
                    size="sm"
                    value={percent}
                />
            </Box>
        </GridItem>
    );
};
